export type CalculateMinedResourceBoostType = {
  effectMultiplier: number;
  startedAt: Date;
  endDate: Date;
};

export type CalculateMindedResourcesData = {
  updatedAt: Date;
  productionRate: number;
  boosts: CalculateMinedResourceBoostType[];
  timePoint?: Date;
  startPoint?: Date;
  returnFloat?: boolean;
};
/**
 *
 * @param plant
 * @returns number
 *
 * здесь расчет ведется не беря во внимание фабрику, на которой может быть лимит сырья.
 * это проверяется в вызывающей функции
 * расчет может быть сделан за любой промежуток времени в будущем
 * в возвращаемое значение не будет включено то, что было уже произведено до начала расчета
 * следовательно и проверка на предмет переполнения здесь не производится
 * если startPoint не указан, берется из updatedAt.
 * Важно, что startPoint не должен быть ранее updatedAt
 * Если timePoint не указан, берется текущее время.
 */
export const calculateMindedResources = (
  plant: CalculateMindedResourcesData
): number => {
  let startPoint: number;

  if (plant.startPoint) {
    if (plant.startPoint < plant.updatedAt) {
      throw new Error("Calculacation cannot be done in the past");
    }
    startPoint = plant.startPoint.getTime();
  } else {
    startPoint = plant.updatedAt.getTime();
  }

  const endPoint = plant.timePoint ? plant.timePoint.getTime() : Date.now();

  if (endPoint < startPoint) {
    throw new Error("Date end cannot be before date start");
  }

  const boosts = plant.boosts.filter(
    (boost: CalculateMinedResourceBoostType): boolean =>
      boost.endDate.getTime() >= startPoint &&
      boost.startedAt.getTime() <= endPoint
  );

  /*
  в timeCheckpoints будут все ключевые точки в timestamp, в которые возможны изменения скорости производства
  сюда включаются точки начала и окончания, все моменты времени из boosts.
  Подобно как вычисляются критические точки производной фукнции
  */
  const timeCheckpoints: number[] = Array.from(
    new Set(
      [
        startPoint,
        endPoint,
        boosts
          .map((boost) => [boost.startedAt.getTime(), boost.endDate.getTime()])
          .flat(),
      ].flat()
    )
  )
    .filter((t) => t >= startPoint && t <= endPoint)
    .sort((a, b) => a - b);

  /*
   в этой переменный временные промежутки и количество ресурсов, которое будет произведено в каждом из них
   */
  const timeSlots: {
    start: Date;
    end: Date;
    mined: number;
  }[] = timeCheckpoints.slice(0, -1).map((startDate: number, nn: number) => {
    const endDate: number = timeCheckpoints[nn + 1];
    const actualBoosts = boosts.filter(
      (boost) =>
        boost.startedAt.getTime() <= startDate &&
        boost.endDate.getTime() >= endDate
    );

    const effectMultiplier = actualBoosts.reduce(
      (acc, current) => acc + current.effectMultiplier,
      0
    );
    const hoursDifference =
      (Math.round(endDate / 1000) - Math.round(startDate / 1000)) / (60 * 60);

    const mined: number =
      plant.productionRate *
      hoursDifference *
      (effectMultiplier === 0 ? 1 : effectMultiplier);

    return {
      start: new Date(startDate),
      end: new Date(endDate),
      mined,
    };
  });

  const minedResources: number = timeSlots.reduce(
    (acc, cur) => acc + cur.mined,
    0
  );

  return plant.returnFloat ? minedResources : Math.floor(minedResources);
};
